import { useEffect, useState } from 'react';
import { api, ApiError, sizeLabel } from '../lib/api';
import { ErrorNote, Modal, useLoad } from './ui';
import NewItemButton from './NewItemButton';

// Search the shared Master Catalog (every NJ product any store has added) and stock an item in this store.
// Product facts come from the master list; price, cost and distributor are entered here and stay private to the store.
export default function MasterSearch({ initialQuery = '', onClose, onAdded }: { initialQuery?: string; onClose: () => void; onAdded: (item: any, product: any) => void }) {
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState<any[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [pick, setPick] = useState<any>(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api<any[]>(`/api/catalog/search?q=${encodeURIComponent(q)}`).then((r) => { setResults(r); setErr(null); }).catch(setErr);
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  if (pick) return <StockItem product={pick} onBack={() => setPick(null)} onClose={onClose} onAdded={onAdded} />;
  return (
    <Modal title="Add from the master list" onClose={onClose} wide>
      <p className="muted">Search every product in the shared NJ catalog. Pick one to add it to your store with your own price, cost and distributor.</p>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or barcode, e.g. bud light 12 pack" aria-label="Search master catalog" />
      <ErrorNote error={err} />
      <div><NewItemButton label="Create a new item" className="primary" start={q.trim() && !/^\d+$/.test(q.trim()) ? { name: q.trim() } : undefined} onCreated={(row) => onAdded(row, { id: row.product_id, brand: row.brand, name: row.name, size_ml: row.size_ml, category: row.category })} /></div>
      {results && results.length === 0 && <div className="empty">Nothing in the master list matches “{q}”. Create it as a new item above.</div>}
      {results && results.length > 0 && (
        <div className="table-wrap" style={{ maxHeight: 360, overflow: 'auto' }}>
          <table className="grid"><tbody>
            {results.map((p) => (
              <tr key={p.id} className="clickable" onClick={() => !p.in_store && setPick(p)} style={p.in_store ? { opacity: 0.55 } : undefined}>
                <td><div style={{ fontWeight: 600 }}>{p.brand} {p.name}</div>
                  <div className="faint">{sizeLabel(p.size_ml)}{p.units_per_pack > 1 ? ` · ${p.units_per_pack}-pack` : ''} · {p.category.replace('_', ' ')}</div></td>
                <td>{p.in_store ? <span className="chip good">In your store</span> : <span className={`chip ${p.status === 'verified' ? 'good' : 'warn'}`}>{p.status}</span>}</td>
              </tr>))}
          </tbody></table>
        </div>)}
    </Modal>
  );
}

function StockItem({ product, onBack, onClose, onAdded }: { product: any; onBack: () => void; onClose: () => void; onAdded: (item: any, product: any) => void }) {
  const wholesalers = useLoad(() => api<any[]>('/api/wholesalers'), []);
  const [f, setF] = useState({ price: '', cost: '', wholesaler: '', reorder: '' });
  const [err, setErr] = useState<unknown>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const priceCents = Math.round(Number(f.price) * 100);
  const costCents = f.cost === '' ? null : Math.round(Number(f.cost) * 100);
  const below = costCents != null && priceCents < costCents;
  const margin = costCents != null && priceCents > 0 ? Math.round(((priceCents - costCents) / priceCents) * 100) : null;
  async function save() {
    setErr(null);
    try {
      const it = await api<any>('/api/items', { body: {
        productId: product.id, retailCents: priceCents, unitCostCents: costCents,
        preferredWholesalerId: f.wholesaler || null, reorderPoint: Number(f.reorder) || 0,
      } });
      onAdded({ ...it, brand: product.brand, name: product.name, size_ml: product.size_ml, category: product.category, on_hand: 0, codes: [] }, product);
    } catch (e) { setErr(e); }
  }
  return (
    <Modal title="Add to your store" onClose={onClose} footer={<><button onClick={onBack}>Back</button><button className="primary" disabled={!(priceCents > 0) || below} onClick={save}>Add item</button></>}>
      <p><b>{product.brand} {product.name}</b> · {sizeLabel(product.size_ml)}{product.units_per_pack > 1 ? ` · ${product.units_per_pack}-pack` : ''}</p>
      <p className="muted">Price, cost and distributor stay private to your store.</p>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Shelf price<input className="num-input" autoFocus inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value.replace(/[^\d.]/g, '') })} placeholder="0.00" /></label>
        <label>Your cost (per unit)<input className="num-input" inputMode="decimal" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value.replace(/[^\d.]/g, '') })} placeholder="0.00" /></label>
      </div>
      {below && <div className="notice bad">Price is under your cost. State liquor law does not allow selling below cost.</div>}
      {margin != null && !below && <p className="faint">Margin {margin}%</p>}
      <div className="field-row">
        <label>Vendor<select value={f.wholesaler} onChange={set('wholesaler')}>
          <option value="">Not sure yet</option>
          {wholesalers.data?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select></label>
        <label>Reorder at<input inputMode="numeric" value={f.reorder} onChange={(e) => setF({ ...f, reorder: e.target.value.replace(/\D/g, '') })} placeholder="0" /></label>
      </div>
      {err instanceof ApiError && err.status === 403 && <p className="faint">A manager has to add new items.</p>}
    </Modal>
  );
}
